import { useEffect, useId, useRef, useState } from 'react';

import { useAppSelector } from '@app/hooks';
import { useUserOptionsQuery } from '@shared/api/lookupsApi';
import { BaseCheckbox } from '@shared/components/base';
import { cn } from '@shared/lib/cn';
import { selectCurrentUser } from '@shared/session/sessionSlice';

import { INBOX_TEXT } from '../constants';
import { hasRecipients, type Recipients } from '../lib/recipients';

/** "Point this query to": every active colleague, then "Directly To Client" (a label only;
 *  the client is never messaged). */
export function RecipientPicker({
  value,
  onChange,
  onDone,
  label = INBOX_TEXT.recipients.label,
  inline = false,
}: {
  value: Recipients;
  onChange: (value: Recipients) => void;
  /** Called when the list closes with someone chosen (moves focus on, as in the prototype). */
  onDone?: () => void;
  label?: string;
  /** Open the list in the flow, pushing what follows down instead of covering it. */
  inline?: boolean;
}) {
  const me = useAppSelector(selectCurrentUser);
  const { data: users } = useUserOptionsQuery(undefined);
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const id = useId();
  const people = (users ?? []).filter((u) => u.isActive && u.id !== me?.id);

  const close = () => {
    setOpen(false);
    if (hasRecipients(value)) onDone?.();
  };
  useEffect(() => {
    // Inline, the list stays open while the query below it is typed.
    if (!open || inline) return undefined;
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
    };
  });

  const names = [
    ...people.filter((u) => value.userIds.includes(u.id)).map((u) => u.fullName),
    ...(value.toClient ? [INBOX_TEXT.recipients.directlyToClient] : []),
  ];
  const toggle = (userId: string, on: boolean) => {
    onChange({
      ...value,
      userIds: on ? [...value.userIds, userId] : value.userIds.filter((u) => u !== userId),
    });
  };

  return (
    // Esc closes just the list, not the dialog around it.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={box}
      className="relative flex flex-col gap-1"
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) {
          e.stopPropagation();
          e.nativeEvent.stopImmediatePropagation();
          close();
        }
      }}
    >
      <span id={`${id}-label`} className="text-[10px] tracking-[0.11em] text-text-2">
        {label}
        <span className="text-crit" aria-hidden>
          {' '}
          *
        </span>
      </span>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={`${id}-label`}
        onClick={() => {
          if (open) close();
          else setOpen(true);
        }}
        className={cn(
          'flex min-h-9 w-full cursor-pointer items-center justify-between gap-2 rounded-app border border-line-strong bg-surface-2 px-2.5 py-1.5 text-left text-[13px]',
          names.length === 0 && 'text-text-3',
        )}
      >
        <span className="truncate">
          {names.length ? names.join(', ') : INBOX_TEXT.recipients.choosePeople}
        </span>
        <i className="pi pi-chevron-down text-[10px] text-text-3" aria-hidden />
      </button>
      {open && (
        <div
          role="listbox"
          aria-multiselectable
          aria-label={label}
          className={cn(
            'flex max-h-72 w-full flex-col gap-1 overflow-y-auto rounded-app border border-line-strong bg-surface p-2',
            inline ? 'mt-1' : 'absolute top-full z-50 mt-1 shadow-app',
          )}
        >
          {people.map((u) => (
            <BaseCheckbox
              key={u.id}
              id={`${id}-${u.id}`}
              checked={value.userIds.includes(u.id)}
              label={u.fullName}
              onChange={(on) => {
                toggle(u.id, on);
              }}
            />
          ))}
          <hr className="my-1 border-line" />
          <BaseCheckbox
            id={`${id}-client`}
            checked={value.toClient}
            label={INBOX_TEXT.recipients.directlyToClient}
            onChange={(on) => {
              onChange({ ...value, toClient: on });
            }}
          />
        </div>
      )}
    </div>
  );
}
